/**
 * 令牌存储。
 *
 * 分工与常见做法不同，这里记录原因：
 * - **access token 只放内存**。它有效期 2 小时，刷新不花钱；一旦落进 localStorage，
 *   XSS 的爆炸半径就从"当前这一页"扩大到"30 天有效的 refresh token"。
 * - **refresh token 落 localStorage**。服务端用的是 `Authorization: Bearer` + 请求体
 *   传令牌，不是 httpOnly cookie，所以刷新令牌本来就必须由 JS 保管；
 *   不落盘的话每次刷新页面都要重新登录，移动端浏览器动不动就回收标签页，不可接受。
 *
 * 存储不可用（隐私模式、被策略禁用）时全部降级为"仅本次会话有效"，绝不抛错：
 * 登录失败的原因应该是"验证码错了"，而不是"localStorage 抛异常"。
 */

import { AuthTokensSchema, type AuthTokens } from '@spirit/contracts';

/** refresh token 的持久化键。access token 不落盘，所以没有对应的键。 */
const REFRESH_TOKEN_KEY = 'spirit.refresh_token';

/** 过期前多久就当作"该刷新了"，用来吸收客户端与服务端的时钟偏差。 */
const EXPIRY_SKEW_MS = 60_000;

export interface TokenSnapshot {
  accessToken: string | null;
  /** epoch 毫秒；`0` 表示未知（例如从存储里刚恢复、还没换过令牌）。 */
  accessTokenExpiresAt: number;
  refreshToken: string | null;
  /** 是否已经尝试过从存储恢复。 */
  hydrated: boolean;
}

const snapshot: TokenSnapshot = {
  accessToken: null,
  accessTokenExpiresAt: 0,
  refreshToken: null,
  hydrated: false,
};

function storage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * 从存储恢复 refresh token。
 *
 * 幂等：重复调用只读一次存储。`hydrated` 置位的意义不只是"读过了"——
 * 它同时告诉 `bootstrapAuth` 可以开始判断登录态了，否则会拿一个还没恢复的
 * 空 refresh token 去判定"未登录"，把已登录用户踢到登录页。
 */
export function hydrateTokens(): void {
  if (snapshot.hydrated) {
    return;
  }
  snapshot.hydrated = true;
  const store = storage();
  if (store === null) {
    return;
  }
  try {
    const raw = store.getItem(REFRESH_TOKEN_KEY);
    if (raw === null) {
      return;
    }
    // 存储里的东西不可信：可能是旧版本写的，也可能被手改过。
    // 用一个最小形状校验，失败就当没登录过——绝不让半残字符串流进请求体。
    const parsed = AuthTokensSchema.pick({ refreshToken: true }).safeParse({ refreshToken: raw });
    if (parsed.success) {
      snapshot.refreshToken = parsed.data.refreshToken;
    } else {
      store.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // 读失败等同没登录。
  }
}

/** 当前 access token；未登录或未刷新时为 null。 */
export function getAccessToken(): string | null {
  return snapshot.accessToken;
}

/** 当前 refresh token；未登录时为 null。 */
export function getRefreshToken(): string | null {
  return snapshot.refreshToken;
}

/**
 * 写入 access token。
 *
 * `expiresInSeconds` 省略时把到期时间置为 0（未知）：`needsRefresh` 会因此返回
 * false，让服务端用 40101 来告诉我们真实情况，而不是拿一个猜出来的时间盲目刷新。
 */
export function setAccessToken(accessToken: string | null, expiresInSeconds = 0): void {
  snapshot.accessToken = accessToken;
  snapshot.accessTokenExpiresAt =
    accessToken !== null && expiresInSeconds > 0 ? Date.now() + expiresInSeconds * 1000 : 0;
}

/** 写入 refresh token（同时落盘）。 */
export function setRefreshToken(refreshToken: string | null): void {
  snapshot.refreshToken = refreshToken;
  const store = storage();
  if (store === null) {
    return;
  }
  try {
    if (refreshToken === null) {
      store.removeItem(REFRESH_TOKEN_KEY);
    } else {
      store.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch {
    // 内存里已经生效，本次会话可正常使用。
  }
}

/** 登录/刷新返回的令牌组合（与契约 §3.1 的 AuthTokens 同形）。 */
export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** 登录或刷新成功后写入整套令牌。 */
export function setSession(tokens: SessionTokens): void {
  setRefreshToken(tokens.refreshToken);
  setAccessToken(tokens.accessToken, tokens.expiresIn);
}

/** 清除全部令牌（登出、或刷新令牌被服务端拒绝）。 */
export function clearSession(): void {
  setAccessToken(null);
  setRefreshToken(null);
}

/** 是否持有令牌。注意：这只说明"本地有令牌"，不代表令牌仍然有效。 */
export function hasSession(): boolean {
  return snapshot.accessToken !== null || snapshot.refreshToken !== null;
}

/**
 * 是否应该在发请求前先刷新。
 *
 * 三种情形：
 * - 没有 access token 但有 refresh token → 要刷（页面刚加载、或只是内存被清空）；
 * - 到期时间已知且已进入 skew 窗口 → 要刷；
 * - 到期时间未知（0）→ **不刷**。宁可让服务端回 40101 再刷新（`sendEnvelope`
 *   会重放一次），也不要每次冷启动都白刷一次令牌。
 */
export function needsRefresh(now: number = Date.now()): boolean {
  if (snapshot.accessToken === null) {
    return snapshot.refreshToken !== null;
  }
  if (snapshot.accessTokenExpiresAt === 0) {
    return false;
  }
  return snapshot.accessTokenExpiresAt - EXPIRY_SKEW_MS <= now;
}

/** 只读快照，供调试与测试断言。 */
export function getTokenSnapshot(): Readonly<TokenSnapshot> {
  return snapshot;
}

/** 仅供测试：清掉全部内存态与持久态。 */
export function resetTokenStoreForTest(): void {
  snapshot.accessToken = null;
  snapshot.accessTokenExpiresAt = 0;
  snapshot.refreshToken = null;
  snapshot.hydrated = false;
  const store = storage();
  if (store !== null) {
    try {
      store.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // 忽略。
    }
  }
}

/** 类型再导出：调用方常常只需要 `AuthTokens` 的形状。 */
export type { AuthTokens };
